// Location service for fetching countries and cities
import { supabase } from '../config/supabase';

export const locationService = {
  // Get all countries
  async getCountries() {
    try {
      const { data, error } = await supabase
        .from('countries')
        .select('*')
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching countries:', error);
      // Return fallback countries
      return [
        { name: 'Pakistan', code: 'PK', currency: 'PKR', currency_symbol: '₨' },
        { name: 'Kuwait', code: 'KW', currency: 'KWD', currency_symbol: 'KD' },
        { name: 'United Arab Emirates', code: 'AE', currency: 'AED', currency_symbol: 'د.إ' },
        { name: 'Saudi Arabia', code: 'SA', currency: 'SAR', currency_symbol: 'ر.س' },
        { name: 'United States', code: 'US', currency: 'USD', currency_symbol: '$' },
        { name: 'United Kingdom', code: 'GB', currency: 'GBP', currency_symbol: '£' },
        { name: 'India', code: 'IN', currency: 'INR', currency_symbol: '₹' },
        { name: 'Bangladesh', code: 'BD', currency: 'BDT', currency_symbol: '৳' },
        { name: 'Canada', code: 'CA', currency: 'CAD', currency_symbol: 'C$' },
        { name: 'Australia', code: 'AU', currency: 'AUD', currency_symbol: 'A$' },
        { name: 'Germany', code: 'DE', currency: 'EUR', currency_symbol: '€' }
      ];
    }
  },

  // Get cities by country
  async getCitiesByCountry(countryName) {
    try {
      // First, get the country code from the countries table
      const { data: countryData, error: countryError } = await supabase
        .from('countries')
        .select('code')
        .eq('name', countryName)
        .single();

      if (countryError || !countryData) {
        console.log('Country not found in database, using fallback');
        throw new Error('Country not found');
      }

      const countryCode = countryData.code;

      // Now get cities for this country code
      const { data, error } = await supabase
        .from('cities')
        .select('name')
        .eq('country_code', countryCode)
        .order('name');

      if (error) throw error;
      return data?.map(city => city.name) || [];
    } catch (error) {
      console.error('Error fetching cities:', error);
      // Return fallback cities
      const fallbackCities = {
        'Pakistan': ['Karachi', 'Lahore', 'Islamabad', 'Rawalpindi', 'Faisalabad', 'Multan', 'Peshawar', 'Quetta', 'Sialkot', 'Gujranwala'],
        'Kuwait': ['Kuwait City', 'Hawalli', 'Salmiya', 'Farwaniya', 'Ahmadi', 'Jahra', 'Mangaf', 'Fahaheel'],
        'United Arab Emirates': ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'],
        'Saudi Arabia': ['Riyadh', 'Jeddah', 'Mecca', 'Medina', 'Dammam'],
        'United States': ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix'],
        'United Kingdom': ['London', 'Manchester', 'Birmingham', 'Liverpool', 'Leeds'],
        'India': ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata'],
        'Bangladesh': ['Dhaka', 'Chittagong', 'Sylhet', 'Rajshahi', 'Khulna'],
        'Canada': ['Toronto', 'Vancouver', 'Montreal', 'Calgary', 'Ottawa'],
        'Australia': ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide'],
        'Germany': ['Berlin', 'Munich', 'Hamburg', 'Frankfurt', 'Cologne']
      };
      return fallbackCities[countryName] || [];
    }
  },

  // Get country code from country name
  getCountryCode(countryName) {
    const countryMap = {
      'Pakistan': 'PK',
      'Kuwait': 'KW',
      'United Arab Emirates': 'AE',
      'Saudi Arabia': 'SA',
      'United States': 'US',
      'United Kingdom': 'GB',
      'India': 'IN',
      'Bangladesh': 'BD',
      'Canada': 'CA',
      'Australia': 'AU',
      'Germany': 'DE',
      'France': 'FR',
      'Italy': 'IT',
      'Spain': 'ES',
      'Japan': 'JP',
      'China': 'CN',
      'Turkey': 'TR',
      'Malaysia': 'MY',
      'Thailand': 'TH',
      'Singapore': 'SG',
      'South Korea': 'KR'
    };
    return countryMap[countryName] || null;
  },

  // Get vendors by location
  async getVendorsByLocation(city, country, vendorType = 'restaurant') {
    try {
      const { data, error } = await supabase
        .rpc('get_vendors_by_location', {
          p_city: city,
          p_country: country,
          p_vendor_type: vendorType
        });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching vendors by location:', error);
      return [];
    }
  },

  // Convert currency
  async convertCurrency(amount, fromCurrency, toCurrency) {
    try {
      const { data, error } = await supabase
        .rpc('convert_currency', {
          p_amount: amount,
          p_from_currency: fromCurrency,
          p_to_currency: toCurrency
        });

      if (error) throw error;
      return data || amount;
    } catch (error) {
      console.error('Error converting currency:', error);
      return amount;
    }
  }
};

export default locationService;
